<template>
  <section
    class="app-ecommerce-details"
    :class="{ 'price-na': isPriceNA }"
  >
    <!-- loaded product info -->
    <template v-if="!loading">
      <!-- Alert: No item found -->
      <b-alert
        variant="danger"
        :show="product === undefined"
      >
        <h4 class="alert-heading">
          Product not found
        </h4>
        <div class="alert-body">
          The product you are looking for is not available. Check
          <b-link
            class="alert-link"
            :to="{ name: 'apps-e-commerce-shop' }"
          >
            Shop
          </b-link>
          for other items.
        </div>
      </b-alert>

      <!-- Content -->
      <b-card
        v-if="product"
        no-body
      >
        <b-card-body>
          <b-row class="my-2">
            <!-- Left: Product Image Container -->
            <b-col
              :aria-disabled="isPriceNA"
              :aria-label="isPriceNA ? 'Price not available. Please contact your brand.' : 'Product image'"
              role="region"
              cols="12"
              md="5"
              class="
                d-flex
                align-items-center
                justify-content-center
                mb-2 mb-md-0
              "
            >
              <div class="d-flex align-items-center justify-content-center">
                <b-carousel
                  id="carousel-interval"
                  ref="carousel_products"
                  class="product-images-carousel"
                  :controls="!isHideCarouselControls"
                  indicators
                  :interval="5000"
                >
                  <b-carousel-slide
                    v-for="(image, index) in parseProductImages(product)"
                    :key="`${image}-${index}`"
                  >
                    <template #img>
                      <video
                        v-if="image && image.includes(productVideoExtension)"
                        width="100%"
                        height="100%"
                        muted
                        controls
                        @mousemove="onHoverVideo"
                        @mouseleave="onHoverVideo"
                      >
                        <source :src="image">
                        Video is not supported.
                      </video>
                      <b-img
                        v-else
                        :src="image"
                        :alt="`${product.name} Gallery`"
                        class="product-img"
                        fluid
                      />
                    </template>
                  </b-carousel-slide>
                </b-carousel>
              </div>
            </b-col>

            <!-- Right: Product Details -->
            <b-col
              cols="12"
              md="7"
            >
              <!-- Product Name -->
              <div
                :aria-disabled="isPriceNA"
                :aria-label="isPriceNA ? 'Product details with unavailable price' : 'Product details'"
                role="region"
                class="product-heading-div"
              >
                <h1>{{ product.name }}</h1>
                <b-card-text
                  v-if="isBrand"
                  class="collectionMore"
                  @click="(e) => {
                    e.preventDefault();
                    showMoreBox();
                  }
                  "
                >
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="20"
                  />
                </b-card-text>
                <b-card
                  v-show="showActionsBox"
                  class="actionsDiv"
                >
                  <div class="infoText">
                    <span>Product's Actions</span>
                  </div>
                  <div
                    class="actionItem"
                    @click="showProductWarningModal"
                  >
                    <b-card-text>
                      <span> Delete Product </span>
                    </b-card-text>
                  </div>
                  <div
                    class="actionItem"
                    @click="copyProductLink()"
                  >
                    <b-card-text>
                      <span> Copy Product's Link </span>
                    </b-card-text>
                  </div>
                  <div
                    v-if="isEditEnabled"
                    class="actionItem"
                    @click="editProduct"
                  >
                    <b-card-text>
                      <span> Edit Product </span>
                    </b-card-text>
                  </div>
                </b-card>
              </div>
              <!-- Department -->
              <b-card-text class="ml-n1">
                <span class="d-inline-block">
                  <b-breadcrumb
                    class="breadcrumb-products mt-1"
                    :items="[
                      product.department,
                      product.category,
                      product.subCategory,
                    ]"
                  />
                </span>
              </b-card-text>
              <!-- Product style code -->
              <b-card-text class="item-company mb-0">
                <span>Style Code: {{ product.styleCode }}</span>
              </b-card-text>
              <!-- KP-4350: tags from the retailer's segment (e.g. MCA) -->
              <v2-segment-tags
                :product="product"
                inline
                class="mt-50"
              />
              <ul class="product-features list-unstyled">
                <div class="row">
                  <div class="col-6">
                    <!-- Pricing -->
                    <li class="product-details-li">
                      <span>
                        Retail:
                        <span class="font-weight-normal">{{
                          formatCurrency(product.retailPrice, product.currency)
                        }}</span>
                      </span>
                    </li>
                    <li
                      v-if="!isGuest && isShowWsp"
                      class="mt-n1"
                    >
                      <span>
                        Wholesale:
                        <span
                          v-if="!product.discountedPrice"
                          class="font-weight-normal"
                          :class="{
                            'text-line-through': product.discountedPrice,
                          }"
                        >
                          {{ formatCurrency(product.price, product.currency) }}
                        </span>
                        <span
                          v-if="product.discountedPrice"
                          class="discounted-price font-weight-bold"
                        >
                          {{
                            formatCurrency(
                              product.discountedPrice,
                              product.currency
                            )
                          }}
                        </span>
                      </span>
                    </li>
                  </div>
                  <div
                    v-if="productDetails.availableCurrencies"
                    class="col-6"
                  >
                    <currency-dropdown
                      :base-currency="collectionBaseCurrency"
                      :selected-currency="selectedCurrency"
                      :currencies="productDetails.availableCurrencies || []"
                      @on-selected-currency="filterByCurrency"
                    />
                  </div>
                </div>
                <hr>
                <li class="product-details-li">
                  <span>
                    Description:
                    <span
                      class="font-weight-normal"
                      v-html="replaceURLTextWithHyperLink(product.descriptionLong)
                      "
                    />
                  </span>
                </li>
                <li class="product-details-li">
                  <span>
                    Material:
                    <span class="font-weight-normal">{{
                      product.descriptionMaterials
                    }}</span>
                  </span>
                </li>
                <li class="product-details-li">
                  <span>
                    Country of Origin:
                    <span class="font-weight-normal">{{
                      product.country
                    }}</span>
                  </span>
                </li>
                <!-- Gender -->
                <li class="product-details-li">
                  <span>
                    Gender:
                    <span class="font-weight-normal">{{ product.gender }}</span>
                  </span>
                </li>
                <li class="product-details-li">
                  <span v-if="product.shippingStartDate && product.shippingEndDate">
                    Availability Date:
                    <span class="font-weight-normal">{{
                      formattedDate(product.shippingStartDate)
                    }}</span>
                    <span class="font-weight-normal">
                      to {{ formattedDate(product.shippingEndDate) }}</span>
                  </span>
                </li>
              </ul>

              <hr>

              <div
                v-if="product.isCarton && !isGuest"
                class="mb-2 d-flex align-items-center"
              >
                <label class="font-small-4">Quantity:</label>
                <b-form-spinbutton
                  v-model="quantity"
                  class="ml-1"
                  min="1"
                  :max="getMaxQuantity(product.quantityAvailable)"
                  inline
                  :disabled="isPriceNA || isBrand || inactiveCollection || isBrandNotAccessible"
                />

                <div
                  v-if="hasProductMaxQuantity(product.quantityAvailable)"
                  class="
                    text-nowrap
                    color-actions-text-grey
                    max-qty-position
                    mt-2-px
                  "
                >
                  Max Qty: {{ product.quantityAvailable }}
                </div>
              </div>
              <!-- Variants -->
              <product-variants
                :items="product.items"
                :is-edit-enabled="true"
                :is-carton="product.isCarton"
                :disabled-product="inactiveCollection || isBrand"
                @update-variants="updateVariants"
              />

              <div class="d-flex">
                <p
                  v-if="productMinQtyPerStyle(product)"
                  class="text-primary mo-style"
                  :class="{
                    'text-danger': isNotValidPerStyle(product, lineTotalQuantity),
                  }"
                >
                  {{ `MOQ per style: ${productMinQtyPerStyle(product)}` }}
                </p>

                <p
                  v-if="productMinQtyPerSize(product)"
                  class="text-primary mo-style"
                  :class="{
                    'text-danger': isNotValidPerSize(product, lineTotalQuantity),
                  }"
                >
                  {{ `MOQ per size: ${productMinQtyPerSize(product)}` }}
                </p>

                <p
                  v-if="segmentMaxQtyLabel(product)"
                  class="text-primary mo-style"
                  :class="{
                    'text-danger': isSegmentQuantityBlocked(product, lineTotalQuantity),
                  }"
                >
                  {{ segmentMaxQtyLabel(product) }}
                </p>
              </div>

              <template v-if="isShowWsp">
                <hr>
                <div class="d-flex flex-column flex-sm-row pt-1">
                  <div
                    v-if="isRetailer"
                    :id="`tooltip_add_to_cart_btn_${product._id}`"
                    v-b-tooltip.hover.bottom="isPriceNA ? 'Price not available. Please contact your brand.' : ''"
                  >
                    <b-button
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      variant="primary"
                      class="btn-cart mr-0 mr-sm-1 mb-1 mb-sm-0"
                      :disabled="isPriceNA || inactiveCollection || isBrandNotAccessible || isNotValidMinQty(product, lineTotalQuantity) || (!product.isInCart && isSegmentQuantityBlocked(product, lineTotalQuantity))"
                      @click="!isPriceNA && reactHandleCartActionClick(product)"
                    >
                      <feather-icon
                        icon="ShoppingCartIcon"
                        class="mr-50"
                      />
                      <span>{{
                        product.isInCart ? 'View In Cart' : 'Add to Cart'
                      }}</span>
                    </b-button>
                  </div>
                  <div>
                    <product-wishlists-dropdown
                      :product="product"
                      :wishlists="wishlists"
                      :is-product-details-page="true"
                      :disabled="inactiveCollection || isBrandNotAccessible"
                      :is-opened-dropdown="isWishlistDropdownOpened"
                      :is-selections="isBrand"
                      :product-wishlists-count="product.wishlists.length"
                      @select-wishlist-on-product="
                        onSelectWishlistOnProduct(product, $event)
                      "
                      @toggle-wishlist-dropdown="
                        onToggleWishlistDropdown(product, $event)
                      "
                      @create-wishlist="
                        createWishlist('create_wishlist_modal_' + product._id)
                      "
                    />
                  </div>
                  <TooltipAddToCartBtn
                    v-if="isRetailer && isBrandNotAccessible && isShowWsp"
                    :target-id="`tooltip_add_to_cart_btn_${product._id}`"
                  />
                </div>
              </template>
            </b-col>
          </b-row>
          <div>
            <div
              v-if="suggestedProducts.length"
              class="ecommerce-application heading-border pt-1"
            >
              <b-card-text class="related-products-title mb-50">
                Recommended for you</b-card-text>
              <section
                ref="cardColumns"
                class="grid-view related-items"
              >
                <template v-for="product of suggestedProducts">
                  <related-product-card
                    :key="product._id"
                    :wishlist-id="wishlistId"
                    :product="product"
                    :wishlists="wishlists"
                    :inactive-collection="inactiveCollection"
                    :is-wishlist-dropdown-opened="isWishlistDropdownOpened"
                    @select-wishlist-on-product="
                      onSelectWishlistOnProduct(product, $event)
                    "
                    @toggle-wishlist-dropdown="
                      onToggleWishlistDropdown(product, $event)
                    "
                    @create-wishlist="
                      createWishlist(
                        'create_wishlist_modal_recommended' + product._id
                      )
                    "
                  />

                  <wishlist-list
                    :key="`create_wishlist_modal_key${product._id}`"
                    :modal-id="'create_wishlist_modal_recommended' + product._id
                    "
                    :is-disable-create-wishlist="false"
                    @move-to-wishlist="
                      reactToggleProductInWishlist({ ...$event, product })
                    "
                  />
                </template>
              </section>
            </div>
            <!-- Infinite Scroll Loading -->
            <section
              v-if="continueLoading"
              class="mt-4"
            >
              <b-row>
                <b-col
                  cols="12"
                  class="text-center"
                >
                  <b-spinner
                    ref="loadingIcon"
                    class="loading-icon"
                    label="Loading..."
                  />
                </b-col>
              </b-row>
            </section>
          </div>
        </b-card-body>
      </b-card>
      <b-modal
        id="modal-select"
        ref="productWarning"
        size="lg"
        modal-class="modal-primary custom-model"
        centered
        hide-header-close
        hide-header
        title="Confirmation Step"
        hide-footer
      >
        <div>
          <div class="pb-1 border-bottom">
            <span class="custom-modal-heading">Confirmation Step </span>
            <feather-icon
              class="float-right close-x-icon"
              icon="XIcon"
              size="24"
              @click="closeProductWarningModal"
            />
          </div>
          <p
            class="mt-1 text-center product-message"
            v-html="productRemoveMessage"
          />
        </div>
        <div>
          <div class="text-center mt-1">
            <b-button
              v-if="canDeleteProduct"
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="reset"
              class="mt-2 mr-2 px-sm-3 font-weight-bolder"
              variant="outline-info"
              @click="closeProductWarningModal"
            >
              Cancel
            </b-button>
            <b-button
              v-show="canDeleteProduct"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="info"
              class="mt-2 px-sm-3 font-weight-bolder"
              :disabled="isSaving"
              @click="removeProduct(true)"
            >
              {{ isSaving ? 'Removing Product...' : 'Yes, remove the product' }}
            </b-button>
            <b-button
              v-show="!canDeleteProduct"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="reset"
              variant="info"
              class="mt-2 px-sm-3 font-weight-bolder"
              :disabled="isSaving"
              @click="closeProductWarningModal"
            >
              Ok! I understand
            </b-button>
          </div>
        </div>
      </b-modal>

      <wishlist-list
        v-if="!!product"
        :modal-id="'create_wishlist_modal_' + product._id"
        :is-disable-create-wishlist="false"
        @move-to-wishlist="reactToggleProductInWishlist({ ...$event, product })"
      />
    </template>
    <!-- loading -->
    <template v-else>
      <div class="text-center p-5 m-5">
        <b-spinner />
      </div>
    </template>
    <product-edit-modal @edit-success="loadProductDetails" />
  </section>
</template>

<script>
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import analytics from '@/@core/utils/analytics'
import {
  apiToastSuccess,
  apiToastWarning,
} from '@/@core/utils/toast'
import { convertToHtml, replaceURLTextWithHyperLink } from '@/common-utils'
import constants, { FLAG_CONSTANTS, wishlistAction } from '@/constants'
import store from '@/store'
import {
  DELETE_COLLECTION_PRODUCT,
} from '@/store/modules/collection.module'
import {
  SET_IS_NO_HOVER_PRODUCT_CARD,
} from '@/store/modules/shop.module'
import {
  formatObject,
  getMaxQuantity,
  hasProductMaxQuantity,
  isProductValueDisabled,
  textTruncate,
} from '@core/utils/utils'
import { useProductDetails } from '@/composables/useProductDetails'
import {
  BAlert,
  BBreadcrumb,
  BButton,
  BCard,
  BCardBody,
  BCardText,
  BCarousel,
  BCarouselSlide,
  BCol,
  BFormSpinbutton,
  BImg,
  BLink,
  BRow,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import CurrencyDropdown from '@/views/components/currency-dropdown/CurrencyDropdown.vue'
import ProductWishlistsDropdown from '@/views/components/product-wishlists-dropdown/ProductWishlistsDropdown.vue'
import { SET_IS_ENABLE_CREATE_WISHLIST } from '@/store/modules/wishlist.module'
import { handleOptimisticUpdateForWishlist } from '@/@core/comp-functions/wishlistUtils'
import Vue from 'vue'
import V2SegmentTags from '@/components/v2/V2SegmentTags.vue'
import UserRoleMixinVue from '../../UserRoleMixin.vue'
import TooltipAddToCartBtn from '../../shop/TooltipAddToCartBtn.vue'
import ProductVariants from './ProductVariants.vue'
import RelatedProductCard from '../other/RelatedProductCard.vue'
import {
  isNotValidMinQty,
  isNotValidPerStyle,
  isNotValidPerSize,
  isSegmentQuantityBlocked,
  productMinQtyPerSize,
  productMinQtyPerStyle,
  segmentMaxQtyLabel,
} from '../../checkout/cart-products-utils'
import WishlistList from '../../wishlist/WishlistList.vue'
import ProductEditModal from '../edit/ProductEditModal.vue'

const { CONNECTION_REQUEST_STATUS } = constants
export default {
  name: 'ProductDetails',
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  components: {
    V2SegmentTags,
    // BSV
    BCard,
    BCardBody,
    BRow,
    BCol,
    BImg,
    BCardText,
    BLink,
    BButton,
    BAlert,
    BBreadcrumb,
    BFormSpinbutton,
    BSpinner,
    BCarousel,
    BCarouselSlide,
    // SFC
    ProductVariants,
    FeatherIcon,
    TooltipAddToCartBtn,
    RelatedProductCard,
    WishlistList,
    CurrencyDropdown,
    ProductWishlistsDropdown,
    ProductEditModal,
  },
  mixins: [UserRoleMixinVue],
  data() {
    return {
      showActionsBox: false,
      isSaving: false,
      canDeleteProduct: false,
      productRemoveMessage: '',
      getMaxQuantity,
      isProductValueDisabled,
      hasProductMaxQuantity,
      replaceURLTextWithHyperLink,
      productVideoExtension: '.mp4',
      isHideCarouselControls: false,
      isNotValidMinQty,
      isNotValidPerStyle,
      isNotValidPerSize,
      isSegmentQuantityBlocked,
      productMinQtyPerSize,
      productMinQtyPerStyle,
      segmentMaxQtyLabel,
      textTruncate,
      wishlistDropdownOpenedCount: 0,
    }
  },
  computed: {
    isPriceNA() {
      return this.product?.price === 'N/A' || this.product?.retailPrice === 'N/A'
    },
    isShowWsp() {
      return !(
        this.isWspHidden
        && this.requestStatus !== CONNECTION_REQUEST_STATUS.APPROVED
      )
    },
    isEditEnabled() {
      return store.state.flags[FLAG_CONSTANTS.PRODUCT_EDIT]
    },
    isWishlistDropdownOpened() {
      return !!this.wishlistDropdownOpenedCount
    },
  },
  created() {
    this.$root.$refs.productDetails = this
  },
  destroyed() {
    analytics.track(
      this.isBrand
        ? constants.TRACKS.ACTIONS.BRAND_END_VIEWING_PRODUCT
        : constants.TRACKS.ACTIONS.RETAILER_END_VIEWING_PRODUCT,
      formatObject(this.productDetails),
    )
    this.$store.commit(SET_IS_NO_HOVER_PRODUCT_CARD, false)
  },
  methods: {
    async editProduct() {
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CLICKS_EDIT_PRODUCT_OPTION, {
        product: this.product,
      })
      const collectionId = this.$router.history?.current?.params?.collectionId
      if (collectionId) {
        try {
          await this.fetchProduct()
          this.$bvModal.show('product-edit-modal')
          this.showActionsBox = false
          analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_VIEWS_PRODUCT_EDIT_MODAL, {
            product: this.product,
          })
        } catch (err) {
          apiToastWarning('Something went wrong while editing product. Please try again later.')
        }
      }
    },
    onToggleWishlistDropdown(product, isOpened = false) {
      this.wishlistDropdownOpenedCount += isOpened ? 1 : -1
      product.isOpenedDropdown = isOpened
    },
    async onSelectWishlistOnProduct(product, wishlist) {
      const payload = {
        product,
        wishlist,
        wishlistId: wishlist._id,
        wishlistName: wishlist.name,
        action: this.isRetailer
          ? wishlistAction.ADD_TO_WISHLIST
          : wishlistAction.ADD_BY_BRAND,
      }
      if (product.wishlists?.includes(wishlist._id)) {
        payload.action = wishlistAction.REMOVE_FROM_WISHLIST
      }

      const updatedProduct = handleOptimisticUpdateForWishlist({
        product: payload.product,
        wishlistId: payload.wishlistId,
        action: payload.action,
      })

      if (this.product._id === updatedProduct._id) {
        this.product = updatedProduct
      } else {
        Vue.set(this.suggestedProducts, this.suggestedProducts.findIndex(p => p._id === updatedProduct._id), updatedProduct)
      }

      await this.reactToggleProductInWishlist(payload)
    },
    createWishlist(modalId) {
      this.$store.commit(SET_IS_ENABLE_CREATE_WISHLIST, true)
      this.$bvModal.show(modalId)
    },
    async createOrUpdateWishlist(product = {}, modalId = '') {
      if (this.wishlistId) {
        const payload = {
          wishlistId: this.wishlistId,
          product,
        }
        if (product?.isInWishlist) {
          payload.action = wishlistAction.REMOVE_FROM_WISHLIST
        } else {
          payload.action = wishlistAction.ADD_TO_WISHLIST
        }
        await this.reactToggleProductInWishlist(payload)
        return
      }
      this.$bvModal.show(modalId)
    },
    onHoverVideo(event) {
      if (event?.target?.paused) {
        this.isHideCarouselControls = false
        this.$refs.carousel_products.start()
      } else {
        this.isHideCarouselControls = true
        this.$refs.carousel_products.pause()
      }
    },
    showMoreBox() {
      this.showActionsBox = !this.showActionsBox
    },
    closeProductWarningModal() {
      this.$refs.productWarning.hide()
    },
    async showProductWarningModal() {
      this.showActionsBox = false
      await this.removeProduct()
    },
    async removeProduct(shouldProceed = false) {
      const params = {}
      if (shouldProceed) {
        params.shouldProceed = true
      }
      this.isSaving = true
      if (this.product._id) {
        this.$store
          .dispatch(DELETE_COLLECTION_PRODUCT, {
            productId: this.product._id,
            params,
          })
          .then(res => {
            apiToastSuccess(res.data.message)
            this.isSaving = false
            this.showActionsBox = false
            this.closeProductWarningModal()
            if (this.$router.history?.current?.params?.collectionId) {
              this.$router.push({
                path: `/products/${this.$router.history.current.params.collectionId}`,
              })
            }
          })
          .catch(err => {
            const { response } = err
            const { data } = response || {}
            const canDelete = data?.data
            const { message } = data
            this.productRemoveMessage = convertToHtml(message)
            if (canDelete?.shouldProceed) {
              this.canDeleteProduct = true
            } else {
              this.canDeleteProduct = false
            }
            this.$refs.productWarning.show()
            this.isSaving = false
          })
      } else {
        this.isSaving = false
        apiToastWarning('Product Not Valid')
      }
    },
    copyProductLink() {
      navigator.clipboard
        .writeText(window.location.href)
        .then(() => {
          apiToastSuccess('Product link copied')
          this.showActionsBox = false
        })
        .catch(err => {
          apiToastWarning(err)
        })
    },
  },
  setup(props, { root }) {
    const composable = useProductDetails(root.$route, root.$router)

    return {
      ...composable,
    }
  },
}
</script>

<style lang="scss">
@import '~@core/scss/base/pages/app-ecommerce-details.scss';
@import '~@core/scss/base/pages/app-shop.scss';
@import '~@core/scss/base/pages/app-ecommerce.scss';
$carousel-arrow-hover-background: #00000032;

.max-qty-position {
  margin-left: 88px;
}

.product-images-carousel {
  &:hover {

    .carousel-control-prev,
    .carousel-control-next {
      background: $carousel-arrow-hover-background;
    }
  }
}

.mo-style {
  padding: 14px;
}

.price-na {
  pointer-events: none;
  opacity: 0.7;
  cursor: not-allowed;
  user-select: none;
}

.price-na .product-img,
.price-na .product-features,
.price-na .product-heading-div {
  pointer-events: none;
}

.price-na button:not([disabled]),
.price-na input:not([disabled]),
.price-na select:not([disabled]) {
  pointer-events: none;
  opacity: 0.5;
  cursor: not-allowed;
}

.price-na button[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: auto;
}
</style>
